/**
 * freeworldBootstrapStore - Centralized store for Freeworld time-period related data
 * 
 * Caches and deduplicates:
 * - time_periods (via fwDataCache unified source)
 * - locations (all, ordered) - TTL: 10 minutes  
 * - freeworld_time_tasks (per period + location) - TTL: 60 seconds
 * - player_time_task_completions (per player + period) - TTL: 60 seconds
 * 
 * Key rules:
 * - If data is null => ALWAYS fetch (ignore TTL)
 * - If force => fetch
 * - If within TTL => return cached data
 * - Cache key includes: playerId, periodId, locationId (where applicable)
 */
import { supabase } from '@/integrations/supabase/client';
import { log, logError } from '@/lib/log';
import { loadTimePeriodsUnified, getCachedTimePeriodsUnified, invalidateTimePeriodsUnified } from '@/lib/fwDataCache';
import type { Tables } from '@/integrations/supabase/types';

type TimePeriod = Tables<'time_periods'>;
type TimeTask = Tables<'freeworld_time_tasks'>;
type Location = Tables<'locations'>;

// Cache TTLs
const CACHE_TTL_SHORT_MS = 60 * 1000;        // 60 seconds for frequently changing data
const CACHE_TTL_LONG_MS = 10 * 60 * 1000;    // 10 minutes for static data (locations, time_periods)

// ============= TIME PERIODS (proxy to fwDataCache) =============

/**
 * Load all time periods (ordered by period_order)
 * PROXY: Delegates to fwDataCache.loadTimePeriodsUnified for unified caching
 */
export async function loadTimePeriods(options?: {
  reason?: string;
  force?: boolean;
}): Promise<TimePeriod[]> {
  const { reason = 'unknown', force = false } = options || {};
  log('[freeworld] time_periods proxy -> fwDataCache', { reason, force });
  return loadTimePeriodsUnified({ reason: `fwBootstrap:${reason}`, force });
}

/**
 * Get cached time periods without triggering a fetch
 */
export function getCachedTimePeriods(): TimePeriod[] | null {
  return getCachedTimePeriodsUnified();
}

// ============= LOCATIONS (global, not player-specific) =============

interface LocationsCache {
  data: Location[];
  at: number;
}

let locationsCache: LocationsCache | null = null;
let locationsInFlight: Promise<Location[]> | null = null;

function isLocationsValid(): boolean {
  if (!locationsCache) return false;
  return Date.now() - locationsCache.at < CACHE_TTL_LONG_MS;
}

/**
 * Load all locations (ordered by display_order)
 */
export async function loadLocations(options?: {
  reason?: string;
  force?: boolean;
}): Promise<Location[]> {
  const { reason = 'unknown', force = false } = options || {};

  // Check cache
  if (!force && locationsCache && isLocationsValid()) {
    log('[freeworld] locations cache hit', { 
      ageMs: Date.now() - locationsCache.at, 
      reason,
      count: locationsCache.data.length
    });
    return locationsCache.data;
  }

  // Check in-flight
  if (locationsInFlight) {
    log('[freeworld] locations in-flight reused', { reason });
    return locationsInFlight;
  }

  // Fetch fresh data
  const start = Date.now();
  locationsInFlight = (async (): Promise<Location[]> => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        logError('[freeworld] locations fetch error:', error);
        return locationsCache?.data || [];
      }

      const locations = data || [];
      
      log('[freeworld] locations fetched', { 
        ms: Date.now() - start, 
        reason,
        count: locations.length
      });

      locationsCache = { data: locations, at: Date.now() };
      return locations;
    } catch (err) {
      logError('[freeworld] locations exception:', err);
      return locationsCache?.data || [];
    } finally {
      locationsInFlight = null;
    }
  })();

  return locationsInFlight;
}

/**
 * Get cached locations without triggering a fetch
 */
export function getCachedLocations(): Location[] | null {
  if (locationsCache && isLocationsValid()) {
    return locationsCache.data;
  }
  return null;
}

// ============= TIME TASKS (per period + optional location) =============

interface TimeTasksEntry {
  data: TimeTask[];
  at: number;
}

// Key format: periodId or periodId:locationId
const timeTasksCache = new Map<string, TimeTasksEntry>();
const timeTasksInFlight = new Map<string, Promise<TimeTask[]>>();

function makeTasksKey(periodId: string, locationId?: string): string {
  return locationId ? `${periodId}:${locationId}` : periodId;
}

function isTimeTasksValid(entry: TimeTasksEntry | undefined): boolean {
  if (!entry) return false;
  return Date.now() - entry.at < CACHE_TTL_SHORT_MS;
}

/**
 * Load time tasks for a specific period (and optionally location)
 */
export async function loadTimeTasks(options: {
  periodId: string;
  locationId?: string;
  reason?: string;
  force?: boolean;
}): Promise<TimeTask[]> {
  const { periodId, locationId, reason = 'unknown', force = false } = options;
  const key = makeTasksKey(periodId, locationId);

  // Check cache
  const cached = timeTasksCache.get(key);
  if (!force && isTimeTasksValid(cached)) {
    log('[freeworld] time_tasks cache hit', { 
      key,
      ageMs: Date.now() - cached!.at, 
      reason,
      count: cached!.data.length
    });
    return cached!.data;
  }

  // Check in-flight
  const inFlight = timeTasksInFlight.get(key);
  if (inFlight) {
    log('[freeworld] time_tasks in-flight reused', { key, reason });
    return inFlight;
  }

  // Fetch fresh data
  const start = Date.now();
  const promise = (async (): Promise<TimeTask[]> => {
    try {
      let query = supabase
        .from('freeworld_time_tasks')
        .select('*')
        .eq('time_period_id', periodId)
        .eq('is_active', true)
        .order('created_at');

      if (locationId) {
        query = query.eq('location_id', locationId);
      }

      const { data, error } = await query;

      if (error) {
        logError('[freeworld] time_tasks fetch error:', error);
        return cached?.data || [];
      }

      const tasks = data || [];
      
      log('[freeworld] time_tasks fetched', { 
        key,
        ms: Date.now() - start, 
        reason,
        tasksCount: tasks.length
      });

      timeTasksCache.set(key, { data: tasks, at: Date.now() });
      return tasks;
    } catch (err) {
      logError('[freeworld] time_tasks exception:', err);
      return cached?.data || [];
    } finally {
      timeTasksInFlight.delete(key);
    }
  })();

  timeTasksInFlight.set(key, promise);
  return promise;
}

/**
 * Get cached time tasks without triggering a fetch
 */
export function getCachedTimeTasks(periodId: string, locationId?: string): TimeTask[] | null {
  const key = makeTasksKey(periodId, locationId);
  const cached = timeTasksCache.get(key);
  if (isTimeTasksValid(cached)) {
    return cached!.data;
  }
  return null;
}

// ============= PLAYER TIME TASK COMPLETIONS (per player + period) =============

interface CompletionsEntry {
  data: string[]; // task IDs
  at: number;
}

// Key format: playerId:periodId
const completionsCache = new Map<string, CompletionsEntry>();
const completionsInFlight = new Map<string, Promise<string[]>>();

function makeCompletionsKey(playerId: string, periodId: string): string {
  return `${playerId}:${periodId}`;
}

function isCompletionsValid(entry: CompletionsEntry | undefined): boolean {
  if (!entry) return false;
  return Date.now() - entry.at < CACHE_TTL_SHORT_MS;
}

/**
 * Load completed task IDs for a player in a specific period
 */
export async function loadTaskCompletions(options: {
  playerId: string;
  periodId: string;
  reason?: string;
  force?: boolean;
}): Promise<string[]> {
  const { playerId, periodId, reason = 'unknown', force = false } = options;
  const key = makeCompletionsKey(playerId, periodId);

  // Check cache
  const cached = completionsCache.get(key);
  if (!force && isCompletionsValid(cached)) {
    log('[freeworld] completions cache hit', { 
      key,
      ageMs: Date.now() - cached!.at, 
      reason,
      count: cached!.data.length
    });
    return cached!.data;
  }

  // Check in-flight
  const inFlight = completionsInFlight.get(key);
  if (inFlight) {
    log('[freeworld] completions in-flight reused', { key, reason });
    return inFlight;
  }

  // Fetch fresh data
  const start = Date.now();
  const promise = (async (): Promise<string[]> => {
    try {
      const { data, error } = await (supabase as any)
        .from('player_time_task_completions')
        .select('task_id')
        .eq('player_id', playerId)
        .eq('time_period_id', periodId);

      if (error) {
        logError('[freeworld] completions fetch error:', error);
        return cached?.data || [];
      }

      const taskIds = (data || []).map((row: { task_id: string }) => row.task_id);
      
      log('[freeworld] completions fetched', { 
        key,
        ms: Date.now() - start, 
        reason,
        completionsCount: taskIds.length
      });

      completionsCache.set(key, { data: taskIds, at: Date.now() });
      return taskIds;
    } catch (err) {
      logError('[freeworld] completions exception:', err);
      return cached?.data || [];
    } finally {
      completionsInFlight.delete(key);
    }
  })();

  completionsInFlight.set(key, promise);
  return promise;
}

/**
 * Get cached completions without triggering a fetch
 */
export function getCachedCompletions(playerId: string, periodId: string): string[] | null {
  const key = makeCompletionsKey(playerId, periodId);
  const cached = completionsCache.get(key);
  if (isCompletionsValid(cached)) {
    return cached!.data;
  }
  return null;
}

/**
 * Add a task ID to the completions cache (after completing a task)
 */
export function addCompletionToCache(playerId: string, periodId: string, taskId: string): void {
  const key = makeCompletionsKey(playerId, periodId);
  const cached = completionsCache.get(key);
  
  if (cached) {
    if (!cached.data.includes(taskId)) {
      cached.data.push(taskId);
      cached.at = Date.now(); // Refresh TTL
      log('[freeworld] completion added to cache', { key, taskId });
    }
  } else {
    // Create new cache entry
    completionsCache.set(key, { data: [taskId], at: Date.now() });
    log('[freeworld] completion cache created', { key, taskId });
  }
}

// ============= COMBINED LOADER =============

export interface FreeworldBootstrapData {
  timePeriods: TimePeriod[];
  timeTasks: TimeTask[];
  completedTaskIds: string[];
}

/**
 * Load all freeworld data for a player/period combination in parallel
 */
export async function loadFreeworldBootstrap(options: {
  playerId: string;
  periodId: string;
  locationId?: string;
  reason?: string;
  force?: boolean;
}): Promise<FreeworldBootstrapData> {
  const { playerId, periodId, locationId, reason = 'unknown', force = false } = options;

  const [timePeriods, timeTasks, completedTaskIds] = await Promise.all([
    loadTimePeriods({ reason, force }),
    loadTimeTasks({ periodId, locationId, reason, force }),
    loadTaskCompletions({ playerId, periodId, reason, force })
  ]);

  return {
    timePeriods,
    timeTasks,
    completedTaskIds
  };
}

// ============= CACHE MANAGEMENT =============

/**
 * Invalidate completions cache for a specific player/period
 */
export function invalidateCompletions(playerId: string, periodId: string): void {
  const key = makeCompletionsKey(playerId, periodId);
  completionsCache.delete(key);
  log('[freeworld] completions cache invalidated', { key });
}

/**
 * Invalidate time tasks cache for a specific period/location
 */
export function invalidateTimeTasks(periodId: string, locationId?: string): void {
  const key = makeTasksKey(periodId, locationId);
  timeTasksCache.delete(key);
  log('[freeworld] time_tasks cache invalidated', { key });
}

/**
 * Invalidate all caches for a specific player/period
 */
export function invalidateFreeworldCache(options: {
  playerId?: string;
  periodId?: string;
}): void {
  const { playerId, periodId } = options;

  if (playerId && periodId) {
    invalidateCompletions(playerId, periodId);
  }
  
  if (periodId) {
    // Invalidate all task caches for this period
    for (const key of timeTasksCache.keys()) {
      if (key.startsWith(periodId)) {
        timeTasksCache.delete(key);
      }
    }
  }
  
  log('[freeworld] cache invalidated', { playerId, periodId });
}

/**
 * Clear all freeworld caches
 */
export function clearFreeworldCache(): void {
  invalidateTimePeriodsUnified();
  timeTasksCache.clear();
  timeTasksInFlight.clear();
  completionsCache.clear();
  completionsInFlight.clear();
  locationsCache = null;
  locationsInFlight = null;
  log('[freeworld] all caches cleared');
}

/**
 * Get cache stats for debugging
 */
export function getFreeworldCacheStats(): {
  timePeriodsCount: number | null;
  timeTasksEntries: number;
  completionsEntries: number;
} {
  const cachedPeriods = getCachedTimePeriodsUnified();
  return {
    timePeriodsCount: cachedPeriods?.length ?? null,
    timeTasksEntries: timeTasksCache.size,
    completionsEntries: completionsCache.size
  };
}
